'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useTransition } from 'react';
function buildSearchUrl(pathname: string, params: URLSearchParams) {
  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

/**
 * Client-side search action for immediate UI feedback and navigation
 * This provides better UX compared to full page reloads
 */
export function useClientSearch() {
  const $ = _c(12);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams) {
    t0 = (query, t1) => {
      const resetPage = t1 === undefined ? true : t1;
      startTransition(() => {
        const params = new URLSearchParams(searchParams);
        if (query && query.trim()) {
          params.set("q", query.trim());
        } else {
          params.delete("q");
        }
        if (resetPage) {
          params.delete("page");
        }
        router.push(buildSearchUrl(pathname, params));
      });
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const performSearch = t0;
  let t1;
  if ($[4] !== pathname || $[5] !== router || $[6] !== searchParams) {
    t1 = () => {
      startTransition(() => {
        const params_0 = new URLSearchParams(searchParams);
        params_0.delete("q");
        params_0.delete("page");
        router.push(buildSearchUrl(pathname, params_0));
      });
    };
    $[4] = pathname;
    $[5] = router;
    $[6] = searchParams;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const clearSearch = t1;
  let t2;
  if ($[8] !== clearSearch || $[9] !== isPending || $[10] !== performSearch) {
    t2 = {
      performSearch,
      clearSearch,
      isSearching: isPending
    };
    $[8] = clearSearch;
    $[9] = isPending;
    $[10] = performSearch;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

/**
 * Enhanced search with loading states and error handling
 */
export function useEnhancedSearch() {
  const $ = _c(6);
  const {
    performSearch,
    clearSearch,
    isSearching
  } = useClientSearch();
  let t0;
  if ($[0] !== performSearch) {
    t0 = async (query, t1) => {
      const resetPage = t1 === undefined ? true : t1;
      ;
      try {
        if (typeof window !== "undefined") {
          document.body.classList.add("search-loading");
        }
        performSearch(query, resetPage);
        setTimeout(_temp, 100);
      } catch (t2) {
        const error = t2;
        console.error("Search error:", error);
        if (typeof window !== "undefined") {
          document.body.classList.remove("search-loading");
        }
        throw error;
      }
    };
    $[0] = performSearch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchWithFeedback = t0;
  let t1;
  if ($[2] !== clearSearch || $[3] !== isSearching || $[4] !== searchWithFeedback) {
    t1 = {
      searchWithFeedback,
      clearSearch,
      isSearching
    };
    $[2] = clearSearch;
    $[3] = isSearching;
    $[4] = searchWithFeedback;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp() {
  if (typeof window !== "undefined") {
    document.body.classList.remove("search-loading");
  }
}